import { apiScopes, getMsalInstance } from '../auth/msalConfig';
import { tokenAcquisitionQueue } from '../utils/tokenAcquisitionQueue';
import { BackendHttpService } from './backendHttpService';

export interface Prompt {
  promptName: string;
  content: string;
  lastUpdated: string;
  lastUpdatedBy: string;
}

export interface UpdatePromptRequest {
  promptName: string;
  content: string;
  updatedBy?: string;
}

export interface ChatResponseModel {
  chatId: string;
  response: string;
  citations: string;
  agentName?: string;
  agentType?: string; // "HELPER" | "OUTPUT"
}

export class AiHttpService extends BackendHttpService {
  constructor() {
    super('api/ai'); // Use backend AI controller endpoint
  }

  async getAllPrompts(): Promise<Prompt[]> {
    const response = await this.get<{ data: Prompt[] }>('prompts');
    return response?.data || [];
  }

  async getPrompt(promptName: string): Promise<Prompt | null> {
    const response = await this.get<{ data: Prompt }>(`prompts/${encodeURIComponent(promptName)}`);
    return response?.data || null;
  }

  async updatePrompt(promptName: string, request: UpdatePromptRequest): Promise<void> {
    await this.put<UpdatePromptRequest, void>(request, `prompts/${encodeURIComponent(promptName)}`);
  }

  /**
   * Stream org chart generation from uploaded DOCX file
   * @param file - DOCX file containing organizational structure
   * @param chatId - Optional chat ID for conversation continuity
   * @returns AsyncIterable of streaming chat responses
   */
  async *streamGenerateOrgChartFromFile(
    file: File,
    chatId?: string
  ): AsyncGenerator<ChatResponseModel, void, unknown> {
    const formData = new FormData();
    formData.append('file', file);
    if (chatId) {
      formData.append('chatId', chatId);
    }

    // Get authentication token using deduplication queue
    const msalInstance = await getMsalInstance();
    const account = msalInstance.getAllAccounts()[0];

    if (!account) {
      throw new Error('No authenticated account found');
    }

    const tokenResponse = await tokenAcquisitionQueue.acquireTokenSilent(
      msalInstance,
      {
        scopes: apiScopes.backendApi,
        account: account,
      }
    );

    const url = this.buildUrl('stream-generate-org-chart-from-file');
    const currentLanguage = localStorage.getItem('language') || 'en';

    const headers: HeadersInit = {
      'Authorization': `Bearer ${tokenResponse.accessToken}`,
      'Accept-Language': currentLanguage,
    };

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to generate org chart: ${response.statusText}. ${errorText}`);
    }

    if (!response.body) {
      throw new Error('Response body is null');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        for (const line of lines) {
          if (line.trim()) {
            try {
              const chunk = JSON.parse(line) as ChatResponseModel;
              yield chunk;
            } catch (e) {
              console.error('Failed to parse chunk:', line, e);
            }
          }
        }
      }

      // Process any remaining buffer
      if (buffer.trim()) {
        try {
          const chunk = JSON.parse(buffer) as ChatResponseModel;
          yield chunk;
        } catch (e) {
          console.error('Failed to parse final chunk:', buffer, e);
        }
      }
    } finally {
      reader.releaseLock();
    }
  }
}
